import { useParams } from 'react-router-dom';
import { ReactNode, useContext } from 'react';
import { CustomLocationContext } from '@client/app/research/custom-location-provider.tsx';
import { useModalManagerContext } from '@packages/ui/modal/modal-manager-context.ts';
import { getFragmentData } from '@client/graphql/types';
import { TableReservationFragment } from '@client/graphql/table-reservation-fragment.ts';
import useTableReservationDetailQuery from '@client/page/management/table-reservation/detail/logic/use-table-reservation-detail-query.ts';
import { useTranslate } from '@tolgee/react';
import Loading from '@packages/ui/loading.tsx';
import ApplicationErrorView from '@client/module/error/application-error-view.tsx';
import AlertError from '@packages/ui/alert/alert-error.tsx';
import dayjs from 'dayjs';
import { ContentLayout, PrimaryContent } from '@packages/ui/content-layout.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import Card from '@packages/ui/card/card.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import moment from 'moment-timezone';
import { useCompanyService } from '@packages/core/company/company-hook.ts';
import ReservationEditModal from '@client/page/management/table-reservation/detail/component/reservation-edit-modal.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import { capitalize } from 'lodash';
import { ReservationTableListConfirmationStatus } from '../list/component/reservation-table-list-confirmation-status';

export default function TableReservationEdit() {
  const reservationID = useParams().id;
  // const navigate = useNavigate();
  const { t } = useTranslate();
  const previousPath = useContext(CustomLocationContext) ?? '';
  const dialogManager = useModalManagerContext();
  // const notificationCenter = useNotificationCenter();
  const company = useCompanyService();

  const { data, loading, error } = useTableReservationDetailQuery(reservationID);
  // const [updateConfirmationStatus] = useUpdateConfirmTableReservation();

  const reservation = getFragmentData(TableReservationFragment, data?.tableReservation);
  const reservationEvent = reservation?.tableReservationEvent;

  if (!reservation) {
    return <AlertError title={'Not found'} message={'Reservation not found'} />;
  }

  // const onUpdateStatusHandler = async (status: TableReservationConfirmationStatus) => {
  //   try {
  //     await updateConfirmationStatus({
  //       variables: {
  //         id: reservation.id,
  //         status: status,
  //       },
  //     });
  //
  //     notificationCenter.showAlert({
  //       status: 'success',
  //       message: t('reservation.edit.alert.message.success'),
  //       title: t('reservation.edit.alert.title'),
  //     });
  //
  //     navigate(-1);
  //   } catch (error) {
  //     notificationCenter.showAlert({
  //       status: 'error',
  //       message: t('reservation.edit.alert.message.error') + extractMessageError(error),
  //       title: t('reservation.edit.alert.title'),
  //     });
  //   }
  // };

  const onUpdateReservation = () => {
    dialogManager.showDialog((onClose) => <ReservationEditModal onClose={onClose} data={reservation} />);
  };

  const cancelReasonString =
    reservation.cancelReason?.reason
      .map((reason) =>
        t('table-reservation.detail.cancel-reason.' + String(reason).toLowerCase(), String(reason).toLowerCase())
      )
      .join(', ') ?? '';

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ApplicationErrorView error={error} />;
  }

  if (!company.company) {
    return <>Something went wrong with your company data</>;
  }

  const backTarget = previousPath.includes('history')
    ? `..?nearestDetailTime=${dayjs(reservation.startTime).toISOString()}`
    : -1;

  return (
    <ContentLayout>
      <PrimaryContent>
        {/*Summary*/}
        <Card>
          <CardHeader title={t('reservation.edit.title')} withBackButton={true} backTarget={backTarget}>
            <PrimaryButton onClick={onUpdateReservation}>{t('common.button.edit')}</PrimaryButton>
          </CardHeader>
          <CardContent>
            <div className={' flex flex-col w-full gap-y-10'}>
              <div className="flex items-center justify-between overflow-hidden">
                <div>
                  <p className="text-gray-600 text-base mb-2">{t('reservation.edit.status')}</p>
                  <ReservationTableListConfirmationStatus item={reservation} showStatusText={true} />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ReservationItem title={t('reservation.edit.name')} description={reservation.guestName} />
                <ReservationItem title={t('reservation.edit.numberGuest')} description={reservation.seats.toString()} />
              </div>

              {reservation.message && (
                <ReservationItem title={t('reservation.edit.message')} description={reservation.message} />
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ReservationItem
                  title={t('reservation.edit.date')}
                  description={moment.tz(reservation.startTime, company.company.settings.timezone).format('YYYY-MM-DD')}
                />

                <ReservationItem
                  title={t('reservation.edit.time')}
                  description={moment.tz(reservation.startTime, company.company.settings.timezone).format('HH:mm')}
                />
              </div>

              {cancelReasonString && (
                <ReservationItem title={t('reservation.edit.cancel-reason')} description={cancelReasonString} />
              )}
            </div>
          </CardContent>
        </Card>

        {/*Contact info*/}
        <Card>
          <CardHeader title={t('reservation.contact-edit.title')} />
          <CardContent>
            <div className="flex flex-col gap-y-8">
              <ReservationItem title={t('reservation.edit.email')} description={reservation.email}>
                <a
                  className="px-4 py-2 border border-gray-300 rounded-lg font-semibold bg-gray-50"
                  href={`mailto:${reservation.email}`}
                >
                  {t('reservation-edit.send-mail', 'Send mail')}
                </a>
              </ReservationItem>
              <ReservationItem title={t('reservation.edit.phone')} description={reservation.phone}>
                <a
                  className="px-4 py-2 border border-gray-300 rounded-lg font-semibold bg-gray-50"
                  href={`tel:${reservation.phone}`}
                >
                  {t('reservation-edit.phone', 'Call now')}
                </a>
              </ReservationItem>
            </div>
          </CardContent>
        </Card>

        {/*Edit history*/}
        <Card>
          <CardHeader title={t('reservation.history-edit.title')} />
          <CardContent>
            {reservation.tableReservationEvent.length > 0 ? (
              <div className="flex flex-col gap-y-6">
                {reservationEvent?.map((event) => {
                  let content = null;

                  if (event.change.length == 0) {
                    content = (
                      <li>
                        {t(
                          'reservation-edit.event.message-change',
                          'Owner has sent a message for customer: {message}',
                          {
                            message: event.message,
                          }
                        )}
                      </li>
                    );
                  } else {
                    content = event.change
                      .filter((change) => change.key != 'endTime')
                      .map((change, index) => {
                        if (change.key == 'startTime') {
                          return (
                            <li key={`${event.id}-${index.toString()}`}>
                              {t('reservation-edit.event.change', 'Change {field} from {old} to {new}', {
                                field: t(`reservation-edit.change-key.${change.key}`),
                                old: moment
                                  .tz(change.old, company.company?.settings.timezone ?? '')
                                  .format('YYYY-MM-DD HH:mm'),
                                new: moment
                                  .tz(change.new, company.company?.settings.timezone ?? '')
                                  .format('YYYY-MM-DD HH:mm'),
                              })}
                            </li>
                          );
                        }
                        return (
                          <li key={`${event.id}-${index.toString()}`}>
                            {t('reservation-edit.event.change', 'Change {field} from {old} to {new}', {
                              field: t(`reservation-edit.change-key.${change.key}`),
                              old: capitalize(change.old ?? ''),
                              new: capitalize(change.new ?? ''),
                            })}
                          </li>
                        );
                      });
                  }

                  return (
                    <div className="space-y-2" key={event.id}>
                      <p className="text-gray-500">{capitalize(t(event.author))}</p>
                      <div className="ml-4">{content}</div>
                      <p className="italic text-gray-600">
                        {moment
                          .tz(event.timestamp, company.company?.settings.timezone ?? 'Europe/Berlin')
                          .format('YYYY-MM-DD HH:mm')}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 italic text-lg">
                {t('reservation-edit.event.empty', 'Reservation event is empty')}
              </p>
            )}
          </CardContent>
        </Card>
      </PrimaryContent>
    </ContentLayout>
  );
}

function ReservationItem(props: { title: string; description: string; children?: ReactNode }) {
  return (
    <div className="flex items-center justify-between overflow-hidden">
      <div>
        <p className="text-gray-600 text-base mb-2">{props.title}</p>
        <p className="text-black text-lg line-clamp-6">{props.description}</p>
      </div>
      <div className="flex items-center justify-end">{props.children}</div>
    </div>
  );
}
